import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import type { Customer } from "../types/store";
import api from "../lib/axios";

interface CustomerState {
  customers: Customer[];
  loading: boolean;
  error: string | null;
  success: boolean;
  hasFetched: boolean;
}

const initialState: CustomerState = {
  customers: [],
  loading: false,
  error: null,
  success: false,
  hasFetched: false,
};

//--------------------------------------------------
// THUNKS
//--------------------------------------------------

// ✅ Thunk to add a customer
export const addCustomer = createAsyncThunk<
  Customer, // Return type
  Omit<Customer, "id" | "created_at" | "updated_at">, // Input type
  { rejectValue: string }
>(
  "customers/addCustomer",
  async (customerData, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/customer/add`,
        customerData
      );
      return response.data.data; // returns the customer object
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Failed to add customer";
      return rejectWithValue(message);
    }
  }
);

// ✅ Thunk to fetch all customers
export const fetchCustomers = createAsyncThunk<
  Customer[],
  void,
  { rejectValue: string }
>("customers/fetchCustomers", async (_, { rejectWithValue }) => {
  try {
    const response = await api.get(`/customer/all`);
    return response.data.data;
  } catch (error: any) {
    const message =
      error.response?.data?.message || "Failed to fetch customers";
    return rejectWithValue(message);
  }
});


// Thunk to delete customers
export const softDeleteCustomers = createAsyncThunk(
  "customers/softDeleteCustomers",
  async (customer_ids: string[], { rejectWithValue }) => {
    try {
      const response = await api.put("/customer/delete", { customer_ids });
      return response.data.message; // e.g., "3 customers successfully deleted"
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Failed to delete customers";
      return rejectWithValue(message);
    }
  }
);

// Thunk to update a customer
export const updateCustomer = createAsyncThunk<
  Customer,
  { customer_id: string; customerData: Partial<Omit<Customer, "id" | "created_at" | "updated_at" | "Sale">> },
  { rejectValue: string }
>(
  "customers/updateCustomer",
  async ({ customer_id, customerData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/customer/update/${customer_id}`, customerData);
      return response.data.customer; // returns the updated customer object
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Failed to update customer";
      return rejectWithValue(message);
    }
  }
);


//--------------------------------------------------
// SLICES
//--------------------------------------------------
const customerSlice = createSlice({
  name: "customers",
  initialState,
  reducers: {
    clearCustomerStatus: (state) => {
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    // Add Customer
    builder
      .addCase(addCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(addCustomer.fulfilled, (state, action: PayloadAction<Customer>) => {
        state.loading = false;
        state.success = true;
        state.customers.unshift(action.payload); // Add new customer to list
      })
      .addCase(addCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to add customer";
      });

    // Fetch Customers
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchCustomers.fulfilled,
        (state, action: PayloadAction<Customer[]>) => {
          state.loading = false;
          state.customers = action.payload;
          state.hasFetched = true;
        }
      )
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch customers";
        state.hasFetched = true;
      });

      // Delete customers
      builder.addCase(softDeleteCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(softDeleteCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        // Filter out deleted customers
        state.customers = state.customers.filter(
          (customer) => !action.payload.data.includes(customer.id)
        );
      })
      .addCase(softDeleteCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Failed to delete customers";
      });

    // Update Customer
    builder
      .addCase(updateCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateCustomer.fulfilled, (state, action: PayloadAction<Customer>) => {
        state.loading = false;
        state.success = true;
        // Update the customer in the list
        const index = state.customers.findIndex((c) => c.id === action.payload.id);
        if (index !== -1) {
          state.customers[index] = action.payload;
        }
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update customer";
      });
  },
});

export const { clearCustomerStatus } = customerSlice.actions;
export const customerReducer = customerSlice.reducer;
